import type { PageScene } from './types';

export const BRIDGE_MESSAGE_TYPE = {
    REQUEST: 'fx.bridge.v1.request',
    RESPONSE: 'fx.bridge.v1.response'
} as const;

export const BRIDGE_INIT_METHOD = 'bridge.init';

export interface BridgeRequestMessage {
    type: typeof BRIDGE_MESSAGE_TYPE.REQUEST;
    clientId?: string;
    requestId: string;
    method: string;
    payload?: unknown;
}

export interface BridgeResponseMessage {
    type: typeof BRIDGE_MESSAGE_TYPE.RESPONSE;
    clientId?: string;
    requestId: string;
    method: string;
    result?: unknown;
    error?: {
        code: string;
        message?: string;
        meta?: Readonly<Record<string, unknown>>;
    };
}

/** Extension Bridge 标准错误码 */
export const ExtensionBridgeErrorCode = {
    PROTOCOL_MISMATCH: 'PROTOCOL_MISMATCH',
    SCENE_MISMATCH: 'SCENE_MISMATCH',
    CAPABILITY_NOT_AVAILABLE: 'CAPABILITY_NOT_AVAILABLE',
    INVALID_ARGUMENT: 'INVALID_ARGUMENT',
    PERMISSION_DENIED: 'PERMISSION_DENIED',
    BRIDGE_DISABLED: 'BRIDGE_DISABLED',
    REQUEST_FAILED: 'REQUEST_FAILED',
    BUSINESS_ERROR: 'BUSINESS_ERROR',
    NO_PREVIEW_DATA: 'NO_PREVIEW_DATA'
} as const;

export type ExtensionBridgeErrorCode =
    (typeof ExtensionBridgeErrorCode)[keyof typeof ExtensionBridgeErrorCode];

/** Extension Bridge 可公开的业务或协议错误 */
export class ExtensionBridgeError extends Error {
    readonly code: ExtensionBridgeErrorCode;
    readonly meta?: Readonly<Record<string, unknown>>;

    /** 创建可返回给 Extension 的业务或协议错误。 */
    constructor(
        code: ExtensionBridgeErrorCode,
        message?: string,
        meta?: Readonly<Record<string, unknown>>
    ) {
        super(message ?? code);
        this.name = 'ExtensionBridgeError';
        this.code = code;
        this.meta = meta;
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isVoidPayload = (payload: unknown): boolean => payload === undefined;

const isOptionalInteger = (value: unknown, min: number, max?: number): boolean => {
    if (value === undefined) {
        return true;
    }
    return (
        typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= min &&
        (max === undefined || value <= max)
    );
};

const isQueryPayload = (payload: unknown): boolean => {
    if (payload === undefined) {
        return true;
    }
    if (!isRecord(payload)) {
        return false;
    }
    return isOptionalInteger(payload.page, 1) && isOptionalInteger(payload.pageSize, 1, 100);
};

const isNonBlankString = (value: unknown): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
};

const hasFormLocator = (payload: Record<string, unknown>): boolean => {
    return isNonBlankString(payload.appId) && isNonBlankString(payload.entryId);
};

const isFormLocatorPayload = (payload: unknown): boolean => {
    return isRecord(payload) && hasFormLocator(payload);
};

const isFormQueryPayload = (payload: unknown): boolean => {
    if (!isRecord(payload) || !hasFormLocator(payload)) {
        return false;
    }
    return isOptionalInteger(payload.page, 1) && isOptionalInteger(payload.pageSize, 1, 100);
};

const hasRecordId = (payload: Record<string, unknown>): boolean => {
    return isNonBlankString(payload.recordId);
};

const isOpenDetailPayload = (payload: unknown): boolean => {
    return isRecord(payload) && hasRecordId(payload);
};

const isFormOpenDetailPayload = (payload: unknown): boolean => {
    return isRecord(payload) && hasFormLocator(payload) && hasRecordId(payload);
};

interface MethodDefinition {
    readonly method: string;
    readonly validatePayload: (payload: unknown) => boolean;
}

const isMethodDefinition = (value: unknown): value is MethodDefinition => {
    return (
        isRecord(value) &&
        typeof value.method === 'string' &&
        typeof value.validatePayload === 'function'
    );
};

// dataSource、form 的参数和结果在两个场景里一致，只按场景拼 method id。
const createDataSourceMethods = (scene: PageScene) => ({
    getWidgets: {
        method: `${scene}.dataSource.getWidgets`,
        validatePayload: isVoidPayload
    },
    getRecords: {
        method: `${scene}.dataSource.getRecords`,
        validatePayload: isQueryPayload
    },
    getSummaryData: {
        method: `${scene}.dataSource.getSummaryData`,
        validatePayload: () => true
    },
    openCreateDialog: {
        method: `${scene}.dataSource.openCreateDialog`,
        validatePayload: isVoidPayload
    },
    openDetailDialog: {
        method: `${scene}.dataSource.openDetailDialog`,
        validatePayload: isOpenDetailPayload
    }
});

const createFormMethods = (scene: PageScene) => ({
    getWidgets: {
        method: `${scene}.form.getWidgets`,
        validatePayload: isFormLocatorPayload
    },
    getRecords: {
        method: `${scene}.form.getRecords`,
        validatePayload: isFormQueryPayload
    },
    openCreateDialog: {
        method: `${scene}.form.openCreateDialog`,
        validatePayload: isFormLocatorPayload
    },
    openDetailDialog: {
        method: `${scene}.form.openDetailDialog`,
        validatePayload: isFormOpenDetailPayload
    }
});

export const bridgeContract = {
    app: {
        customPage: {
            scene: 'APP.customPage',
            methods: {
                context: {
                    get: {
                        method: 'APP.customPage.context.get',
                        validatePayload: isVoidPayload
                    }
                },
                dataSource: createDataSourceMethods('APP.customPage'),
                form: createFormMethods('APP.customPage')
            }
        },
        customDetailPage: {
            scene: 'APP.customDetailPage',
            methods: {
                context: {
                    get: {
                        method: 'APP.customDetailPage.context.get',
                        validatePayload: isVoidPayload
                    }
                },
                dataSource: createDataSourceMethods('APP.customDetailPage'),
                form: createFormMethods('APP.customDetailPage'),
                currentRecord: {
                    getWidgets: {
                        method: 'APP.customDetailPage.currentRecord.getWidgets',
                        validatePayload: isVoidPayload
                    },
                    get: {
                        method: 'APP.customDetailPage.currentRecord.get',
                        validatePayload: isVoidPayload
                    }
                }
            }
        }
    }
} as const;

const getSceneMethods = (scene: PageScene): object => {
    switch (scene) {
        case 'APP.customPage':
            return bridgeContract.app.customPage.methods;
        case 'APP.customDetailPage':
            return bridgeContract.app.customDetailPage.methods;
        default: {
            const unknownScene: never = scene;
            throw new ExtensionBridgeError(
                ExtensionBridgeErrorCode.INVALID_ARGUMENT,
                `未知场景 (${String(unknownScene)})`
            );
        }
    }
};

const findMethodDefinition = (group: object, method: string): MethodDefinition | undefined => {
    for (const value of Object.values(group)) {
        if (isMethodDefinition(value)) {
            if (value.method === method) {
                return value;
            }
            continue;
        }
        if (isRecord(value)) {
            const found = findMethodDefinition(value, method);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
};

/** 按场景查找方法定义。找不到表示该 method 不属于这个场景。 */
export const findSceneMethod = (scene: PageScene, method: string): MethodDefinition | undefined => {
    return findMethodDefinition(getSceneMethods(scene), method);
};

type SceneHandler = (payload: unknown) => unknown | Promise<unknown>;

const isSceneHandler = (value: unknown): value is SceneHandler => {
    return typeof value === 'function';
};

const bindHandlers = (
    specs: Record<string, unknown>,
    handlers: Record<string, unknown>,
    registered: Map<string, SceneHandler>,
    path: string
): void => {
    for (const key of Object.keys(handlers)) {
        const handlerPath = path ? `${path}.${key}` : key;
        const spec = specs[key];
        const handler = handlers[key];
        if (spec === undefined) {
            throw new ExtensionBridgeError(
                ExtensionBridgeErrorCode.INVALID_ARGUMENT,
                `当前场景不能注册该 Handler (${handlerPath})`
            );
        }
        if (handler === undefined) {
            continue;
        }
        if (isMethodDefinition(spec)) {
            if (!isSceneHandler(handler)) {
                throw new ExtensionBridgeError(
                    ExtensionBridgeErrorCode.INVALID_ARGUMENT,
                    `Handler 必须是函数 (${spec.method})`
                );
            }
            registered.set(spec.method, handler);
            continue;
        }
        if (!isRecord(spec) || !isRecord(handler)) {
            throw new ExtensionBridgeError(
                ExtensionBridgeErrorCode.INVALID_ARGUMENT,
                `当前场景不能注册该 Handler (${handlerPath})`
            );
        }
        bindHandlers(spec, handler, registered, handlerPath);
    }
};

/** 按契约树登记 handler。只复制创建时的函数，之后修改传入对象不会生效。 */
export const registerSceneHandlers = (
    scene: PageScene,
    handlers: object
): ReadonlyMap<string, SceneHandler> => {
    if (!isRecord(handlers)) {
        throw new ExtensionBridgeError(
            ExtensionBridgeErrorCode.INVALID_ARGUMENT,
            'handlers 必须是对象'
        );
    }
    const registered = new Map<string, SceneHandler>();
    bindHandlers(getSceneMethods(scene) as Record<string, unknown>, handlers, registered, '');
    return registered;
};
